import React from 'react';
import { Line, Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import ShareButton from './ShareButton';
import './ResultsDashboard.css';

// Register ChartJS components including ArcElement for Pie chart
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ResultsDashboard = ({ results }) => {
  if (!results) return null;

  // SAFE: Destructure with defaults
  const { 
    summary = {}, 
    monthly_breakdown = [], 
    recommendations = [], 
    inflation_rate = 0 
  } = results;

  // SAFE: Check if we have data
  if (!monthly_breakdown || monthly_breakdown.length === 0) {
    return (
      <div className="results-dashboard">
        <div className="error-message">
          <p>⚠️ No simulation data available. Please try again.</p>
        </div>
      </div>
    );
  }

  // Calculate scenario variations (optimistic/pessimistic)
  const baseInflationRate = inflation_rate / 100;
  const optimisticRate = Math.max(0.01, baseInflationRate - 0.02);
  const pessimisticRate = baseInflationRate + 0.02;

  const calculateScenario = (inflationMultiplier) => {
    return monthly_breakdown.map((month, index) => {
      const monthlyInflationFactor = Math.pow(1 + (baseInflationRate * inflationMultiplier), index / 12);
      return month.total_expenses * monthlyInflationFactor / Math.pow(1 + baseInflationRate, index / 12);
    });
  };

  const baselineExpenses = monthly_breakdown.map(m => m.total_expenses || 0);
  const optimisticExpenses = calculateScenario(optimisticRate / baseInflationRate);
  const pessimisticExpenses = calculateScenario(pessimisticRate / baseInflationRate);

  // Prepare data for expense trend chart with SHADED BAND
  const expenseTrendData = {
    labels: monthly_breakdown.map(m => m.date || ''),
    datasets: [
      {
        label: 'Worst Case',
        data: pessimisticExpenses,
        borderColor: 'rgba(239, 68, 68, 0.3)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: '+1',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 1,
        borderDash: [5, 5]
      },
      {
        label: 'Expected',
        data: baselineExpenses,
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.15)',
        fill: '+1',
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 0
      },
      {
        label: 'Best Case',
        data: optimisticExpenses,
        borderColor: 'rgba(34, 197, 94, 0.3)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 1,
        borderDash: [5, 5]
      }
    ]
  };

  const lastMonth = monthly_breakdown[monthly_breakdown.length - 1];
  const categoryData = {
    labels: Object.keys(lastMonth.inflated_expenses_by_category || {}).map(
      cat => cat.charAt(0).toUpperCase() + cat.slice(1)
    ),
    datasets: [
      {
        label: 'Monthly Cost',
        data: Object.values(lastMonth.inflated_expenses_by_category || {}),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(168, 85, 247, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(107, 114, 128, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(239, 68, 68)',
          'rgb(34, 197, 94)',
          'rgb(168, 85, 247)',
          'rgb(249, 115, 22)',
          'rgb(236, 72, 153)',
          'rgb(107, 114, 128)'
        ],
        borderWidth: 2
      }
    ]
  };

  const pieChartData = {
    labels: Object.keys(lastMonth.inflated_expenses_by_category || {}).map(
      cat => cat.charAt(0).toUpperCase() + cat.slice(1)
    ),
    datasets: [
      {
        data: Object.values(lastMonth.inflated_expenses_by_category || {}),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(168, 85, 247, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(107, 114, 128, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(239, 68, 68)',
          'rgb(34, 197, 94)',
          'rgb(168, 85, 247)',
          'rgb(249, 115, 22)',
          'rgb(236, 72, 153)',
          'rgb(107, 114, 128)'
        ],
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      y: {
        beginAtZero: false
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: $${value.toFixed(0)} (${percentage}%)`;
          }
        }
      }
    }
  };

  // NEW: Calculate top 3 impact categories from expense data
  const topImpactCategories = Object.entries(lastMonth.inflated_expenses_by_category || {})
    .map(([category, amount]) => ({
      category,
      monthly_amount: amount,
      // Estimate impact based on category (housing/healthcare inflate faster)
      impact: category === 'housing' ? amount * 1.3 : 
              category === 'healthcare' ? amount * 1.3 : 
              category === 'food' ? amount * 1.2 : amount
    }))
    .sort((a, b) => b.impact - a.impact)
    .slice(0, 3);

  return (
    <div className="results-dashboard">
      <div className="dashboard-header">
        <h2>📊 Simulation Results</h2>
        <div className="inflation-badge">
          Current Inflation: {inflation_rate}%
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="card card-red">
          <div className="card-icon">💸</div>
          <div className="card-content">
            <h3>Annual Expense Increase</h3>
            <p className="card-value">${(summary.total_annual_expense_increase || 0).toLocaleString()}</p>
            <small>How much more you'll spend this year</small>
          </div>
        </div>

        <div className="card card-orange">
          <div className="card-icon">📉</div>
          <div className="card-content">
            <h3>Purchasing Power Loss</h3>
            <p className="card-value">{(summary.purchasing_power_loss_pct || 0).toFixed(2)}%</p>
            <small>Your money buys less each month</small>
          </div>
        </div>

        <div className="card card-blue">
          <div className="card-icon">💼</div>
          <div className="card-content">
            <h3>Raise Needed</h3>
            <p className="card-value">{(summary.income_increase_needed_pct || 0).toFixed(1)}%</p>
            <small>To maintain current lifestyle</small>
          </div>
        </div>

        <div className="card card-purple">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <h3>Savings Reduction</h3>
            <p className="card-value">${(summary.annual_savings_reduction || 0).toLocaleString()}</p>
            <small>Less money saved this year</small>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <h3>📈 Monthly Expense Trend</h3>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '16px' }}>
            Shaded area shows possible range based on inflation variability (±2%)
          </p>
          <div className="chart-wrapper">
            <Line data={expenseTrendData} options={chartOptions} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px', marginBottom: '30px' }}>
          <div className="chart-container">
            <h3>💵 Category Breakdown (Dollars)</h3>
            <div className="chart-wrapper">
              <Bar data={categoryData} options={chartOptions} />
            </div>
          </div>

          <div className="chart-container">
            <h3>🥧 Expense Distribution (%)</h3>
            <div className="chart-wrapper">
              <Pie data={pieChartData} options={pieOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* HIGHEST IMPACT CATEGORIES - ADDED BACK */}
      <div className="highest-impact-section">
        <h3>🔥 Highest Impact Categories</h3>
        <div className="impact-cards-row">
          {topImpactCategories.map((cat, index) => (
            <div key={index} className="impact-card-yellow">
              <div className="impact-icon">
                {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
              </div>
              <div className="impact-details">
                <h4>{cat.category.charAt(0).toUpperCase() + cat.category.slice(1)}</h4>
                <p className="impact-amount">${cat.monthly_amount.toFixed(0)}/month</p>
                <p className="impact-label">Impact: {cat.impact.toFixed(0)}</p>
                <small>Inflation multiplier: 1.{cat.category === 'housing' || cat.category === 'healthcare' ? '3' : cat.category === 'food' ? '2' : '0'}x</small>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="recommendations-section">
          <h3>💼 Personalized Recommendations</h3>
          <ul className="recommendations-list">
            {recommendations.map((rec, index) => (
              <li key={index}>{rec}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Share Section */}
      <div className="share-section">
        <ShareButton results={results} />
      </div>
    </div>
  );
};

export default ResultsDashboard;
